"use client";

import type { VirtualizerOptions } from "@tanstack/react-virtual";
import * as React from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

import { cn } from "@acme/ui";
import { useTreeKeyboardNavigation } from "@acme/ui/hooks/use-tree-keyboard-navigation";
import { ScrollArea, ScrollBar } from "@acme/ui/scroll-area";

export interface TreeItem {
  type: string;
  name: string;
  path: string;
  children?: TreeItem[];
  data?: Record<string, unknown>; // Additional data that can be attached to tree items
}

export interface TreeNode extends TreeItem {
  level: number;
  isExpanded?: boolean;
}

export interface TreeItemProps {
  item: TreeNode;
  index: number;
  isFocused: boolean;
  onItemClick: (index: number, item: TreeNode) => void;
}

export interface TreeProps {
  data: TreeItem[];
  renderItem: (props: TreeItemProps) => React.ReactNode;
  onItemClick?: (item: TreeNode) => void;
  expandedItems?: Set<string>;
  onExpandedItemsChange?: (expandedItems: Set<string>) => void;
  autoFocusOnMount?: boolean;
  itemHeight?: number;
  overscan?: number;
  virtualizerOptions?: Partial<VirtualizerOptions<HTMLDivElement, Element>>;
  className?: string;
  emptyState?: React.ReactNode;
  indent?: number;
  isExpandAll?: boolean;
}

function createFlatTreeStructure(
  items: TreeItem[],
  level = 0,
  currentExpandedItems: Set<string>,
): TreeNode[] {
  const flattenedNodes: TreeNode[] = [];

  for (const item of items) {
    const treeNode: TreeNode = {
      type: item.type,
      name: item.name,
      path: item.path,
      level,
      children: item.children,
      data: item.data,
    };

    if (item.children && item.children.length > 0) {
      treeNode.isExpanded = currentExpandedItems.has(item.path);
    }

    flattenedNodes.push(treeNode);

    if (
      item.children &&
      item.children.length > 0 &&
      currentExpandedItems.has(item.path)
    ) {
      flattenedNodes.push(
        ...createFlatTreeStructure(
          item.children,
          level + 1,
          currentExpandedItems,
        ),
      );
    }
  }

  return flattenedNodes;
}

function getAllExpandableItems(items: TreeItem[]): string[] {
  const expandableItems: string[] = [];

  function traverse(nodes: TreeItem[]) {
    for (const node of nodes) {
      if (node.children && node.children.length > 0) {
        expandableItems.push(node.path);
        traverse(node.children);
      }
    }
  }

  traverse(items);
  return expandableItems;
}

const Tree = React.forwardRef<HTMLDivElement, TreeProps>(
  (
    {
      data,
      renderItem,
      onItemClick,
      expandedItems: controlledExpandedItems,
      onExpandedItemsChange,
      autoFocusOnMount = false,
      itemHeight = 24,
      overscan = 10,
      virtualizerOptions = {},
      className,
      emptyState,
      indent = 12,
      isExpandAll = false,
      ...props
    },
    _ref,
  ) => {
    const scrollElementRef = React.useRef<HTMLDivElement>(null);
    const treeContainerRef = React.useRef<HTMLDivElement>(null);

    const allExpandableItems = React.useMemo(
      () => getAllExpandableItems(data),
      [data],
    );

    const [uncontrolledExpandedItems, setUncontrolledExpandedItems] =
      React.useState<Set<string>>(() => {
        return isExpandAll ? new Set(allExpandableItems) : new Set();
      });

    React.useEffect(() => {
      if (
        isExpandAll &&
        uncontrolledExpandedItems.size === 0 &&
        !controlledExpandedItems
      ) {
        setUncontrolledExpandedItems(new Set(allExpandableItems));
      }
    }, [
      allExpandableItems,
      controlledExpandedItems,
      data,
      isExpandAll,
      uncontrolledExpandedItems.size,
    ]);

    const currentExpandedItems =
      controlledExpandedItems ?? uncontrolledExpandedItems;

    const virtualizedTreeNodes = React.useMemo(() => {
      return createFlatTreeStructure(data, 0, currentExpandedItems);
    }, [data, currentExpandedItems]);

    const virtualizer = useVirtualizer({
      count: virtualizedTreeNodes.length,
      getScrollElement: () => scrollElementRef.current,
      estimateSize: () => itemHeight,
      overscan,
      ...virtualizerOptions,
    });

    const toggleItemExpansion = React.useCallback(
      (path: string) => {
        if (onExpandedItemsChange) {
          const updatedExpandedItems = new Set(currentExpandedItems);
          if (updatedExpandedItems.has(path)) {
            updatedExpandedItems.delete(path);
          } else {
            updatedExpandedItems.add(path);
          }
          onExpandedItemsChange(updatedExpandedItems);
        } else {
          setUncontrolledExpandedItems((previousExpandedItems) => {
            const updatedExpandedItems = new Set(previousExpandedItems);
            if (updatedExpandedItems.has(path)) {
              updatedExpandedItems.delete(path);
            } else {
              updatedExpandedItems.add(path);
            }
            return updatedExpandedItems;
          });
        }
      },
      [currentExpandedItems, onExpandedItemsChange],
    );

    const { focusedIndex, handleKeyDown, handleItemClick } =
      useTreeKeyboardNavigation(
        virtualizedTreeNodes,
        toggleItemExpansion,
        onItemClick,
        virtualizer,
        autoFocusOnMount,
      );

    const isInitialRender = React.useRef(true);

    React.useEffect(() => {
      if (isInitialRender.current) {
        isInitialRender.current = false;
        return;
      }

      if (virtualizedTreeNodes.length > 0 && treeContainerRef.current) {
        treeContainerRef.current.focus();
      }
    }, [virtualizedTreeNodes.length]);

    React.useEffect(() => {
      const measureTimeout = setTimeout(() => virtualizer.measure(), 10);
      return () => clearTimeout(measureTimeout);
    }, [currentExpandedItems, virtualizer]);

    if (virtualizedTreeNodes.length === 0) {
      return (
        <div className={cn("flex h-full flex-col", className)}>
          {emptyState}
        </div>
      );
    }

    const sizes = { full: indent, half: Math.round(indent / 2) };

    return (
      <div
        ref={treeContainerRef}
        className={cn("flex h-full flex-col focus:outline-none", className)}
        onKeyDown={handleKeyDown}
        onClick={() => treeContainerRef.current?.focus()}
        tabIndex={0}
        role="tree"
        aria-label="Tree view"
        {...props}
      >
        <ScrollArea ref={scrollElementRef} className="flex-1">
          <div
            style={{
              height: virtualizer.getTotalSize(),
              position: "relative",
              width: "100%",
            }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const rowIndex = virtualRow.index;
              const treeNode = virtualizedTreeNodes[rowIndex];
              if (!treeNode) return null;

              const rowStyle: React.CSSProperties = {
                height: virtualRow.size,
                width: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                transform: `translateY(${virtualRow.start}px)`,
                "--tree-indent": `${sizes.half + treeNode.level * sizes.half}px`,
              } as React.CSSProperties;

              return (
                <div
                  key={treeNode.path}
                  data-index={rowIndex}
                  ref={virtualizer.measureElement}
                  style={rowStyle}
                  className="group"
                >
                  {renderItem({
                    item: treeNode,
                    index: rowIndex,
                    isFocused: rowIndex === focusedIndex,
                    onItemClick: handleItemClick,
                  })}
                </div>
              );
            })}
          </div>
          <ScrollBar className="z-50" />
        </ScrollArea>
      </div>
    );
  },
);

Tree.displayName = "Tree";

export { Tree };
